import type { components, operations } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

/**
 * The typed boundary to the budget endpoints under `/api/accounting` (Wave 3
 * stream D).
 *
 * Every type here is GENERATED — `components['schemas']['BudgetResource']`
 * and friends come from `openapi.json`. AGENTS.md §3: a type describing an
 * API response is generated, never authored. Two corrections on top of that:
 *
 * - `name` on the store/update requests: Scramble reads `['required',
 *   'array', …]` off the FormRequest and infers `string[]`, but the column is
 *   jsonb keyed by locale and the API expects `{ en, ar }` — the same
 *   inference gap `FixedAssetStoreRequest` has in `accounting-assets/api.ts`.
 * - `BudgetVarianceRow` is derived from the `budget.varianceReport` response
 *   (Scramble now types the `BudgetVarianceQuery::run()` rows).
 *
 * The `X-Company-Id` header is what `SetTenantContext` reads to populate
 * `CompanyContext`. These routes carry no `{company}` path segment, so the
 * header is the only thing that says which tenant this is.
 */

export type Budget = components['schemas']['BudgetResource']
export type BudgetLine = components['schemas']['BudgetLineResource']
export type Account = components['schemas']['AccountResource']
export type FiscalPeriod = components['schemas']['FiscalPeriodResource']

/** The three lifecycle states (the backend's closed budget status enum). */
export const BUDGET_STATUSES = ['draft', 'approved', 'archived'] as const

export type BudgetStatus = (typeof BUDGET_STATUSES)[number]

/**
 * The budgets list's URL-backed filters.
 *
 * `GET /accounting/budgets` exposes no query parameters (`budget.index` is
 * `query?: never` in the generated contract), so both are matched client-side
 * over the fetched rows; the route owns the values so a filtered view is
 * linkable and survives a reload.
 */
export interface BudgetFilters {
  q?: string
  status?: BudgetStatus
}

/** A fiscal year with its periods, as `GET /accounting/fiscal-periods` lists them. */
export type FiscalYearWithPeriods =
  operations['fiscalPeriod.index']['responses'][200]['content']['application/json']['data'][number]

export interface BudgetLineBody {
  account_id: string
  cost_center_id?: string | null
  fiscal_period_id: string
  amount: string
  currency: string
}

export interface BudgetBody {
  fiscal_year_id: string
  name: Record<string, string>
  lines: BudgetLineBody[]
}

/**
 * One account's budget, actual and variance. `type` is the `AccountType`
 * value — `readVariance` words the row from it.
 */
export type BudgetVarianceRow =
  operations['budget.varianceReport']['responses'][200]['content']['application/json']['data'][number]

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time — the same ref pattern as
 * `features/accounting-bills/api.ts`.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Prefix shared by every budget query key, so one invalidation hits all. */
function budgetsKey(companyId: string | null) {
  return ['companies', companyId, 'accounting', 'budgets'] as const
}

/** The chart of accounts, for the grid's account rows. Same endpoint, key and
 * shape as `features/accounting/api.ts` — the features share the cache,
 * never a module import (AGENTS.md: a feature never imports another
 * feature).
 */
export function useAccounts(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Account[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'accounts'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Account[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/accounts', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load accounts')
      return unwrap<Account[]>(response.data ?? {}, 'accounts') ?? []
    },
  })
}

/** Same endpoint, key and shape as `features/accounting/api.ts` `useFiscalYears`. */
export function useFiscalYears(
  companyId: string | null,
  options?: Omit<UseQueryOptions<FiscalYearWithPeriods[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FiscalYearWithPeriods[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'fiscal-periods'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<FiscalYearWithPeriods[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/fiscal-periods', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load fiscal years')
      return unwrap<FiscalYearWithPeriods[]>(response.data ?? {}, 'fiscal-periods') ?? []
    },
  })
}

// Cost-center options come from the Accounting cost-center list hook.

export function useBudgets(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Budget[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Budget[], Error>({
    queryKey: budgetsKey(companyId),
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Budget[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/budgets', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load budgets')
      return unwrap<Budget[]>(response.data ?? {}, 'budgets') ?? []
    },
  })
}

export function useCreateBudget(
  companyId: string | null,
  options?: Omit<UseMutationOptions<Budget, Error, BudgetBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Budget, Error, BudgetBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/budgets', {
        headers: { 'X-Company-Id': currentCompany() },
        // Schema artifact: `name: string[]` vs `{ en, ar }`.
        body: body as unknown as components['schemas']['StoreBudgetRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create budget')
      return unwrap<Budget>(response.data ?? {}, 'budgets')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: budgetsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useUpdateBudget(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<Budget, Error, { budgetId: string; body: BudgetBody }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Budget, Error, { budgetId: string; body: BudgetBody }>({
    ...options,
    mutationFn: async ({ budgetId, body }) => {
      const response = await api.PATCH('/accounting/budgets/{budget}', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { budget: budgetId } },
        // Schema artifact: `name: string[]` vs `{ en, ar }`.
        body: body as unknown as components['schemas']['UpdateBudgetRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to update budget')
      return unwrap<Budget>(response.data ?? {}, 'budgets')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: budgetsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useBudgetVariance(
  companyId: string | null,
  fiscalYearId: string,
  costCenterId: string,
  options?: Omit<UseQueryOptions<BudgetVarianceRow[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BudgetVarianceRow[], Error>({
    queryKey: [
      'companies',
      companyId,
      'accounting',
      'budget-variance',
      fiscalYearId,
      costCenterId,
    ] as const,
    enabled: Boolean(companyId && fiscalYearId),
    ...options,
    queryFn: async (): Promise<BudgetVarianceRow[]> => {
      if (!companyId || !fiscalYearId) return []
      const response = await api.GET('/accounting/reports/budget-variance', {
        headers: { 'X-Company-Id': companyId },
        params: {
          query: {
            fiscal_year_id: fiscalYearId,
            ...(costCenterId ? { cost_center_id: costCenterId } : {}),
          },
        },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load the variance report')
      }
      return unwrap<BudgetVarianceRow[]>(response.data ?? {}, 'budget-variance') ?? []
    },
  })
}

export type ExportFormat = 'csv' | 'pdf'

/**
 * The variance export endpoint's URL. The typed client cannot express it
 * usefully (the body is a file, not JSON), so the path is built here and
 * fetched by `downloadFile` in `src/lib` — the same escape hatch as the
 * report pack's `reportExportUrl`.
 */
export function budgetVarianceExportUrl(
  fiscalYearId: string,
  costCenterId: string,
  format: ExportFormat,
): string {
  const params = new URLSearchParams()
  params.set('format', format)
  params.set('fiscal_year_id', fiscalYearId)
  if (costCenterId) params.set('cost_center_id', costCenterId)
  return `${apiOrigin}/api/accounting/reports/budget-variance/export?${params.toString()}`
}

/** Trigger the authenticated CSV/PDF download for the variance report. */
export async function budgetVarianceExport(
  companyId: string,
  fiscalYearId: string,
  costCenterId: string,
  format: ExportFormat,
): Promise<void> {
  const url = budgetVarianceExportUrl(fiscalYearId, costCenterId, format)
  await downloadFile(url, `budget-variance.${format}`, { 'X-Company-Id': companyId })
}
